import { BackButton } from "@/components/BackButton";
import { CourseHighlight } from "@/components/CourseHighlight";
import { PianoVideos } from "@/components/PianoVideos";
import { PracticeSchedule } from "@/components/PracticeSchedule";
import { SunMoon, FileText, Layers } from "lucide-react";
import { Logo } from "@/components/Logo";
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, CheckCircle2, Clock, Lock, Sparkles, Headphones } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { lessonTrack } from "@/data/playalong";
import { PlayAlongNote } from "@/components/PlayAlongPromo";
import { IntroSplash } from "@/components/IntroSplash";
import { getInstrument, getCourse } from "@/data/curriculum";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { AskTutor } from "@/components/AskTutor";
import { CourseFeedback } from "@/components/CourseFeedback";
import { GospelUnlock, MelodyFirst } from "@/components/GospelCourse";
import { Breadcrumbs, CourseTabs } from "@/components/CourseNav";
import { OneDeviceNotice } from "@/components/OneDeviceNotice";

const READ_NOTE = {
  guitarists: ["Stave and TAB:", "as a guitarist, you should be able to read notes off a stave and on TAB. This course shows you both, side by side, so you learn the differences between them."],
  singers: ["Stave, keyboard and words:", "as a singer, you should be able to read notes off a stave, find them on a keyboard and follow the words underneath. This course shows you all three, side by side, so you learn how they work together."],
  drummers: ["Stave and drum score:", "as a drummer, you should be able to read drum score and know how it relates to the stave. This course shows you both, side by side, so you learn the differences between them."],
};


export default function CourseDetail() {
  const { instrumentId, courseId } = useParams();
  const instrument = getInstrument(instrumentId);
  const course = getCourse(instrumentId, courseId);
  const { user, progress, purchases } = useAuth();
  const completed = new Set(progress?.completed_lessons || []);
  const owned = (purchases || []).includes(`${instrumentId}:${courseId}`);
  const [showIntro, setShowIntro] = useState(true);
  useEffect(() => { window.scrollTo({ top: 0 }); }, [courseId]);

  if (!instrument || !course) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="mx-auto max-w-3xl px-4 py-20 text-center">
          <p className="font-serif text-2xl">Course not found.</p>
          <Link to="/courses" className="mt-4 inline-block text-primary">Back to courses</Link>
        </div>
      </div>
    );
  }

  const doneCount = course.lessons.filter((l) => completed.has(l.id)).length;
  const sibling = course.mini ? null : instrument.courses.find((c) => c.id !== courseId);
  const LEVELS = ["Beginner", "Intermediate", "Advanced"];
  const levelCounts = LEVELS
    .map((lv) => ({ level: lv, count: course.lessons.filter((l) => l.level === lv).length }))
    .filter((x) => x.count > 0);

  return (
    <div className="min-h-screen text-foreground">
      <AnimatePresence>
        {showIntro && (
          <IntroSplash
            eyebrow={`${instrument.label} · ${course.tagline}`}
            title={course.title}
            subtitle={course.blurb}
            ctaLabel="Begin course"
            onEnter={() => setShowIntro(false)}
            testId="course-intro-splash"
            back={`/instruments/${instrumentId}`}
          />
        )}
      </AnimatePresence>
      <Navbar />
      <div className="mx-auto max-w-7xl px-4 sm:px-8 py-10 lg:py-14">
        <div className="mb-4"><BackButton testId="course-back-button" fallback={`/instruments/${instrumentId}`} /></div>
        <Breadcrumbs hideBack items={[{ label: "All courses", to: "/courses" }, { label: instrument.label, to: `/instruments/${instrumentId}` }, { label: course.mini ? course.title : `${course.from} → ${course.to}` }]} />
        <CourseTabs instrument={instrument} currentId={courseId} />

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <span className="rounded-full bg-secondary px-4 py-1.5 font-mono text-xs uppercase tracking-widest text-primary">
            {instrument.label}
          </span>
          <span className="rounded-full bg-primary/10 px-4 py-1.5 font-mono text-xs uppercase tracking-widest text-primary">
            {course.tagline}
          </span>
          <span className="text-sm text-muted-foreground">{doneCount}/{course.lessons.length} completed</span>
        </div>
        <div className="mt-6"><div className="mb-3" data-testid="course-logo"><Logo height={56} /></div></div>
        <h1 className="mt-2 font-serif text-4xl tracking-tight sm:text-5xl">{course.title}</h1>
        <p className="mt-4 max-w-2xl text-muted-foreground">{course.blurb}</p>
        <OneDeviceNotice className="mt-4 max-w-2xl" testId="course-one-device-notice" />
        {READ_NOTE[instrumentId] && ["foundations", "mastery"].includes(courseId) && (
          <p className="mt-4 max-w-2xl rounded-xl border border-primary/40 bg-primary/5 px-5 py-3.5 text-base text-foreground" data-testid={`course-${instrumentId}-read-note`}>
            <b className="text-primary">{READ_NOTE[instrumentId][0]}</b> {READ_NOTE[instrumentId][1]}
          </p>
        )}
        <div className="max-w-4xl"><CourseHighlight instrumentId={instrumentId} courseId={courseId} title={`${instrument.label} · ${course.mini ? course.title : course.tagline}`} /></div>
        <PracticeSchedule instrumentId={instrumentId} weeks={course.lessons.length} />
        <PianoVideos limit={3} compact instrumentId={instrumentId} />
        <PlayAlongNote testId="course-playalong-note" />
        {(owned || user?.role === "admin") && (
          <div className="mt-4 flex flex-wrap gap-2">
            <Link to={`/practice-paper/${instrumentId}/${courseId}`} data-testid="course-practice-paper"><Button variant="outline" className="rounded-full"><FileText size={15} className="mr-1.5" /> Theory practice paper</Button></Link>
            <Link to="/terms" data-testid="course-terms-link"><Button variant="outline" className="rounded-full"><Layers size={15} className="mr-1.5" /> Music terms flashcards</Button></Link>
          </div>
        )}
        <p className="mt-4 flex items-center gap-3 rounded-xl border border-border bg-secondary px-5 py-3.5 text-base text-foreground" data-testid="theme-tip"><SunMoon size={22} className="shrink-0 text-primary" /> Tip: press the light/dark mode button (sun/moon) at the top if it's easier to read in the room you're in — or open Accessibility (bottom left) for bigger text, captions and read-aloud.</p>

        {/* Free syllabus overview — no payment needed */}
        <div className="mt-8 rounded-2xl border border-border bg-card p-6" data-testid="course-syllabus">
          <div className="flex flex-wrap items-center gap-3">
            <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">Free syllabus preview</span>
            <span className="text-sm text-muted-foreground">
              Takes you from <strong className="text-foreground">{course.from}</strong> to <strong className="text-foreground">{course.to}</strong> across {course.lessons.length} lessons
            </span>
          </div>
          <div className="mt-4 flex flex-wrap gap-2" data-testid="syllabus-levels">
            {levelCounts.map((x) => (
              <span key={x.level} className="rounded-full border border-border bg-background px-3 py-1.5 text-xs text-muted-foreground">
                <span className="font-mono text-primary">{x.count}</span> {x.level} {x.count === 1 ? "lesson" : "lessons"}
              </span>
            ))}
          </div>
          {sibling && (
            <div className="mt-5 rounded-xl border border-dashed border-border p-4" data-testid="course-chooser">
              <p className="text-sm font-medium text-foreground">Not sure where to start?</p>
              <p className="mt-1 text-sm text-muted-foreground">
                This course covers <strong className="text-foreground">{course.from} → {course.to}</strong>. If you're already confident reading at {course.from.toLowerCase()} level, you may prefer{" "}
                <Link to={`/courses/${instrumentId}/${sibling.id}`} data-testid="sibling-course-link" className="font-medium text-primary underline underline-offset-2">
                  {sibling.title}
                </Link>{" "}
                ({sibling.tagline}). Both syllabuses are free to preview before you buy.
              </p>
            </div>
          )}

          {!course.mini && <div className="mt-4 flex items-start gap-3 rounded-xl bg-primary/10 p-4" data-testid="recommended-hint">
            <Sparkles size={18} className="mt-0.5 shrink-0 text-primary" />
            <p className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">Recommended for you: </span>
              {course.from === "Beginner"
                ? "if reading music is new to you — or you'd like to rebuild your fundamentals from scratch — this is the perfect place to start. No prior theory needed."
                : `best if you can already name notes on the staff and read simple rhythms. If reading is still new to you, begin with ${sibling ? sibling.title : "the Foundations course"} first, then come back here.`}
            </p>
          </div>}
        </div>

        <MelodyFirst />
        {!owned && course.mini && <GospelUnlock courseKey={`${instrumentId}:${course.id}`} />}
        {!owned && !course.mini && (
          <div className="mt-6 flex flex-col items-start justify-between gap-4 rounded-2xl border border-border bg-primary/10/50 p-5 sm:flex-row sm:items-center" data-testid="course-unlock-banner">
            <div className="flex items-center gap-3">
              <Lock size={20} className="text-primary" />
              <p className="text-sm text-muted-foreground">
                Unlock all {course.lessons.length} lessons — <span className="font-semibold text-foreground">£500</span>, or <span className="font-semibold text-foreground">£450</span> with a referral code from someone who's already booked, or bundle any two courses (even across different instruments) for <span className="font-semibold text-foreground">£850</span> (£425 each).
              </p>
            </div>
            <Link to={`/payment?instrument=${instrumentId}&course=${courseId}#courses`} data-testid="course-enrol-button">
              <Button className="rounded-full bg-primary text-foreground hover:bg-primary/90">Pay now</Button>
            </Link>
          </div>
        )}

        <h2 className="mt-12 font-serif text-2xl">Lesson-by-lesson breakdown</h2>
        <p className="mt-1 text-sm text-muted-foreground">Exactly what each of the {course.lessons.length} lessons covers — free to read before you enrol.</p>
        <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {course.lessons.map((lesson, i) => {
            const isDone = completed.has(lesson.id);
            const isFree = false;
            const locked = !owned;
            const CardTag = locked ? "div" : Link;
            const linkProps = locked
              ? {}
              : { to: `/lessons/${lesson.id}` };
            return (
              <motion.div
                key={lesson.id}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: (i % 3) * 0.06 }}
              >
                <CardTag
                  {...linkProps}
                  data-testid={`lesson-card-${lesson.id}`}
                  className={`group flex h-full flex-col rounded-2xl border border-border bg-card p-6 transition-all ${
                    locked ? "opacity-70" : "hover:-translate-y-1 hover:border-primary hover:shadow-xl hover:shadow-amber-900/5"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary font-serif text-lg text-foreground">
                      {i + 1}
                    </span>
                    {isDone ? (
                      <span className="flex items-center gap-1 rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-medium text-emerald-600 dark:text-emerald-400">
                        <CheckCircle2 size={13} /> Done
                      </span>
                    ) : locked ? (
                      <span className="flex items-center gap-1 rounded-full bg-secondary px-3 py-1 text-xs font-medium text-muted-foreground">
                        <Lock size={12} /> Locked
                      </span>
                    ) : isFree ? (
                      <span className="rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-medium text-emerald-600 dark:text-emerald-400">Free</span>
                    ) : (
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-medium ${
                          lesson.level === "Beginner"
                            ? "bg-primary/10 text-primary"
                            : lesson.level === "Advanced"
                            ? "bg-secondary text-primary"
                            : "bg-muted text-muted-foreground"
                        }`}
                      >
                        {lesson.level}
                      </span>
                    )}
                  </div>
                  <h3 className="mt-4 font-serif text-xl leading-snug group-hover:text-primary">{lesson.title}</h3>
                  <p className="mt-2 text-sm font-medium text-muted-foreground">{lesson.topic}</p>
                  {lesson.summary && <p className="mt-2 flex-1 text-xs leading-relaxed text-muted-foreground/80">{lesson.summary}</p>}
                  <div className="mt-4 flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Clock size={13} /> {lesson.minutes} min
                    {lessonTrack(lesson.id) && <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-primary/15 px-2 py-0.5 font-semibold text-primary" data-testid={`lesson-playalong-tag-${lesson.id}`}><Headphones size={11} /> Play-along · {lessonTrack(lesson.id).track.title}</span>}
                  </div>
                </CardTag>
              </motion.div>
            );
          })}
        </div>

        {owned && doneCount === course.lessons.length && (
          <div className="mt-16 flex flex-col items-start justify-between gap-4 rounded-3xl border border-primary/40 bg-primary/10 p-6 sm:flex-row sm:items-center" data-testid="course-certificate-cta">
            <div>
              <p className="font-serif text-2xl">Course complete — congratulations!</p>
              <p className="mt-1 text-sm text-muted-foreground">Your certificate is ready, with your knowledge-check average.</p>
            </div>
            <Link to={`/certificate/${instrumentId}/${courseId}`} data-testid="course-certificate-link">
              <Button className="rounded-full bg-primary px-6 text-primary-foreground hover:bg-primary/90">View certificate</Button>
            </Link>
          </div>
        )}

        {(owned || user?.role === "admin") && (
          <div className="mt-16 grid gap-6 lg:grid-cols-2" data-testid="course-end-section">
            <AskTutor instrumentId={instrumentId} courseId={courseId} />
            <CourseFeedback instrumentId={instrumentId} courseId={courseId} defaultName={user?.name || ""} />
          </div>
        )}
      </div>
    </div>
  );
}
